import React from "react";
import styled from "styled-components";
import moment from "moment";
import { Skeleton } from "@mui/material";
import { TextView } from "../../../components";

const PayoutsWrapper = styled.div`
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  background-color: #ffffff;
  height: 100%;
  border: 1px solid #e1e1e1;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  gap: 8px;
`;

const PayoutsTitle = styled(TextView)`
  font-weight: 700;
  font-size: 15px;
  color: #565556;
  white-space: nowrap;
  flex-shrink: 0;
`;

const UpdatedText = styled(TextView)`
  font-size: 10px;
  color: #565556;
  font-style: italic;
  margin-left: auto;
  text-align: right;
  flex-shrink: 1;
  white-space: nowrap;
`;

const PriorMonthCard = styled.div`
  background-color: #faffce;
  border-radius: 8px;
  padding: 12px;
`;

const CurrentMonthCard = styled.div`
  background-color: #efe9fe;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Amount = styled(TextView)`
  font-weight: 500;
  font-size: 18px;
  color: #123051;
  flex-shrink: 0;
`;

const InfoText = styled(TextView)`
  font-size: 13px;
  color: #123051;
  margin-top: 8px;
  font-weight: 500;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #b9d645;
  margin: 8px 0;
`;

const PayoutsCard = ({ data, loading = false }) => {
  if (loading) {
    return (
      <PayoutsWrapper>
        <HeaderRow>
          <Skeleton variant="text" width={80} height={20} />
          <Skeleton variant="text" width={180} height={16} />
        </HeaderRow>

        <PriorMonthCard>
          <Row>
            <Skeleton variant="text" width={100} height={18} />
            <Skeleton variant="text" width={60} height={24} />
          </Row>
          <Divider />
          <Skeleton
            variant="text"
            width={120}
            height={18}
            style={{ marginTop: 8 }}
          />
          <Skeleton
            variant="text"
            width={140}
            height={18}
            style={{ marginTop: 8 }}
          />
        </PriorMonthCard>

        <CurrentMonthCard>
          <Skeleton variant="text" width={150} height={18} />
          <Skeleton variant="text" width={60} height={24} />
        </CurrentMonthCard>
      </PayoutsWrapper>
    );
  }

  const { previous_month, current_month } = data || {};
  const paidOutDate = moment().subtract(1, "months").format("MM/15");

  // Show the "subject to change" notice only before or on the 15th of the current month
  const showSubjectToChange = moment().date() <= 15;

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    return `$${(Number(amount) / 100).toFixed(2)}`;
  };

  return (
    <PayoutsWrapper>
      <HeaderRow>
        <PayoutsTitle>Payouts</PayoutsTitle>
        <UpdatedText>*Updated daily before midnight PST</UpdatedText>
      </HeaderRow>

      <PriorMonthCard>
        <Row>
          <TextView style={{ fontWeight: 700, color: "#565556", fontSize: 13 }}>
            Prior Month
          </TextView>
          <Amount>{formatAmount(previous_month?.total)}</Amount>
        </Row>
        <Divider />
        <InfoText>Last Payout {paidOutDate}</InfoText>
        {showSubjectToChange && (
          <InfoText style={{ fontStyle: "italic", color: "#565556" }}>
            Amount is subject to change
          </InfoText>
        )}
      </PriorMonthCard>

      <CurrentMonthCard>
        <TextView style={{ fontWeight: 700, color: "#565556", fontSize: 13 }}>
          Current Month's Estimate
        </TextView>
        <Amount>{formatAmount(current_month?.total)}</Amount>
      </CurrentMonthCard>
    </PayoutsWrapper>
  );
};

export default PayoutsCard;
